import React, { useContext, useEffect } from "react";
import { CartContext } from "../context/CartContext";
import { useCustomer } from "../context/CustomerContext";
import { useWishlist } from "../context/WishlistContext";
import useCheckoutLoyalty from "../hooks/useCheckoutLoyalty";
import { formatKopecks } from "../utils/loyalty";
import { getProductDisplayName } from "../utils/productDisplayName";
import CheckoutForm from "../components/CheckoutForm";
import CheckoutLoyaltyPanel from "../components/CheckoutLoyaltyPanel";
import { FaHeart, FaRegHeart, FaTrash, FaMinus, FaPlus } from "react-icons/fa";
import styles from "./Cart.module.css";

const priceFormatter = new Intl.NumberFormat("uk-UA", {
  maximumFractionDigits: 2,
});

const formatPrice = (value) => `${priceFormatter.format(value)} ₴`;

const Cart = () => {
  const { cartItems, removeFromCart, updateItemQuantity, refreshCartAvailability, priceNotice, getTotalCartPrice } =
    useContext(CartContext);
  const { customer } = useCustomer();
  const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlist();
  const loyalty = useCheckoutLoyalty({ cartItems, customer });
  const clientCartTotal = getTotalCartPrice();
  const loyaltyQuote = loyalty.quote;
  const hasAuthoritativeTotals = Boolean(
    loyaltyQuote?.hasAuthoritativeTotals,
  );
  const displayedProductsTotal = hasAuthoritativeTotals
    ? formatKopecks(loyaltyQuote.productsBeforeDiscountKopecks)
    : formatPrice(clientCartTotal);
  const displayedFinalTotal = hasAuthoritativeTotals
    ? formatKopecks(loyaltyQuote.payableItemsKopecks)
    : formatPrice(clientCartTotal);

  useEffect(() => {
    refreshCartAvailability().catch(() => undefined);
  }, [refreshCartAvailability]);

  const stockInsufficient = cartItems.some((item) =>
    Number.isFinite(Number(item.maxAvailable)) &&
    Number(item.quantity) > Number(item.maxAvailable));

  const handleIncrease = (sku, weight, qty) => {
    updateItemQuantity(sku, weight, qty + 1);
  };

  const handleDecrease = (sku, weight, qty) => {
    if (qty > 1) {
      updateItemQuantity(sku, weight, qty - 1);
    }
  };

  if (cartItems.length === 0) {
    return (
      <div className={styles.cartContainer}>
        <h1 className={styles.cartHeader}>Ваш кошик</h1>
        <p className={styles.emptyState}>Ваш кошик порожній :(</p>
      </div>
    );
  }

  return (
    <div className={styles.cartContainer}>
      <div className={styles.cartHeadRow}>
        <h1 className={styles.cartHeader}>Ваш кошик</h1>
      </div>

      {priceNotice && <p role="status">{priceNotice}</p>}
      <div className={styles.cartLayout}>
        <section className={styles.itemsList} aria-label="Товари у кошику">
          {cartItems.map((item) => {
            const regularPrice = Number(item.price) || 0;
            const promotionalPrice = Number(item.discountPrice);
            const hasDiscount =
              Number.isFinite(promotionalPrice) &&
              promotionalPrice > 0 &&
              promotionalPrice < regularPrice;
            const currentPrice = hasDiscount ? promotionalPrice : regularPrice;
            const lineTotal = currentPrice * item.quantity;
            const oldLineTotal = item.price * item.quantity;
            const displayName = getProductDisplayName(item);
            const manufacturer = item.manufacturer || "—";
            const productId = item.productId || item._id || "";
            const isFavorite = productId ? isInWishlist(productId) : false;
            const discountPercent = hasDiscount
              ? Math.round(((regularPrice - promotionalPrice) / regularPrice) * 100)
              : 0;

            return (
              <article
                className={styles.itemCard}
                key={`${item.productCode}-${item.weight}`}
              >
                <div className={styles.itemImageWrap}>
                  <img
                    src={item.image}
                    alt={item.name}
                    className={styles.productImage}
                    loading="lazy"
                    decoding="async"
                  />
                </div>

                <div className={styles.itemMain}>
                  <p className={styles.productName}>{displayName}</p>
                  <p className={styles.metaRow}>
                    <span>Артикул: {item.productCode || "—"}</span>
                    <span className={styles.metaSeparator} aria-hidden="true">
                      |
                    </span>
                    <span>Виробник: {manufacturer}</span>
                  </p>

                  <div className={styles.itemActions}>
                    <button
                      type="button"
                      className={`${styles.itemActionButton} ${styles.favoriteButton} ${
                        isFavorite ? styles.favoriteButtonActive : ""
                      }`}
                      onClick={() =>
                        isFavorite
                          ? removeFromWishlist(productId)
                          : addToWishlist({ ...item, _id: productId })
                      }
                      disabled={!productId}
                      aria-label={
                        isFavorite ? "Видалити з обраного" : "Додати в обране"
                      }
                    >
                      {isFavorite ? (
                        <FaHeart aria-hidden="true" />
                      ) : (
                        <FaRegHeart aria-hidden="true" />
                      )}
                      <span>{isFavorite ? "В обраному" : "В обране"}</span>
                    </button>

                    <button
                      type="button"
                      className={`${styles.itemActionButton} ${styles.removeButton}`}
                      onClick={() =>
                        removeFromCart(item.productCode, item.weight)
                      }
                      aria-label="Видалити товар з кошика"
                    >
                      <FaTrash aria-hidden="true" />
                      <span>Видалити</span>
                    </button>
                  </div>
                </div>

                <div className={styles.itemAside}>
                  <div className={styles.itemPriceSummary}>
                    {hasDiscount ? (
                      <>
                        <div className={styles.oldPriceRow}>
                          <span className={styles.oldPrice}>
                            {formatPrice(oldLineTotal)}
                          </span>
                          <span className={styles.discountBadge}>
                            -{discountPercent}%
                          </span>
                        </div>
                        <strong className={styles.discountPrice}>
                          {formatPrice(lineTotal)}
                        </strong>
                      </>
                    ) : (
                      <strong className={styles.currentPrice}>
                        {formatPrice(lineTotal)}
                      </strong>
                    )}
                  </div>

                  <div className={styles.quantityControls}>
                    <button
                      type="button"
                      className={styles.decrease}
                      onClick={() =>
                        handleDecrease(
                          item.productCode,
                          item.weight,
                          item.quantity,
                        )
                      }
                      disabled={item.quantity <= 1}
                      aria-label="Зменшити кількість"
                    >
                      <FaMinus />
                    </button>
                    <span aria-live="polite">{item.quantity}</span>
                    <button
                      type="button"
                      className={styles.increase}
                      onClick={() =>
                        handleIncrease(
                          item.productCode,
                          item.weight,
                          item.quantity,
                        )
                      }
                      aria-label="Збільшити кількість"
                    >
                      <FaPlus />
                    </button>
                  </div>
                </div>
              </article>
            );
          })}
        </section>

        <aside className={styles.sidebar}>
          {stockInsufficient && (
            <p role="alert" style={{ color: "#a12222", fontWeight: 600 }}>
              Кількість одного з товарів перевищує актуальний залишок одного складу. Зменште кількість перед оформленням.
            </p>
          )}

          {loyalty.conditionsNotice && <p role="alert">{loyalty.conditionsNotice}</p>}
          <CheckoutLoyaltyPanel
            settings={loyalty.settings}
            quote={loyalty.quote}
            requestedBonusKopecks={loyalty.requestedBonusKopecks}
            loading={loyalty.loading}
            error={loyalty.error}
            onRequestedBonusKopecksChange={loyalty.setRequestedBonusKopecks}
            onUseMaximum={loyalty.useMaximum}
            onCancel={loyalty.cancelRedemption}
            onRetry={() =>
              loyalty.refreshQuote({
                bonusKopecks: loyalty.requestedBonusKopecks,
              }).catch(() => undefined)
            }
          />

          <div className={styles.summaryCard}>
            <div className={styles.summaryInfo}>
              <p className={styles.itemsCount}>
                Товарів у кошику: <strong>{cartItems.length}</strong>
              </p>
            </div>
            <div className={styles.cartSummary}>
              <div className={styles.summaryRow}>
                <span>Товари</span>
                <strong>{displayedProductsTotal}</strong>
              </div>
              {hasAuthoritativeTotals &&
                loyaltyQuote.productDiscountKopecks !== null &&
                loyaltyQuote.productDiscountKopecks > 0 && (
                  <div className={styles.summaryRow}>
                    <span>Знижки на товари</span>
                    <strong className={styles.discountValue}>
                      −{formatKopecks(loyaltyQuote.productDiscountKopecks)}
                    </strong>
                  </div>
                )}
              {hasAuthoritativeTotals && loyaltyQuote.appliedBonusKopecks > 0 && (
                <div className={styles.summaryRow}>
                  <span>Використані бонуси</span>
                  <strong className={styles.discountValue}>
                    −{formatKopecks(loyaltyQuote.bonusDiscountKopecks)}
                  </strong>
                </div>
              )}
              <div className={styles.summaryRow}>
                <span>Доставка</span>
                <strong>за обраним способом</strong>
              </div>
              <div className={styles.summaryDivider} />
              <p className={styles.summaryLabel}>До оплати за товари</p>
              <div className={styles.cartTotal}>{displayedFinalTotal}</div>
              {hasAuthoritativeTotals &&
                customer &&
                loyalty.settings?.earningEnabled && loyaltyQuote.estimatedEarnBonusKopecks > 0 && (
                  <p className={styles.earnHint}>
                    За це замовлення ви отримаєте {formatKopecks(loyaltyQuote.estimatedEarnBonusKopecks)} бонусів після його успішного завершення.
                  </p>
                )}
            </div>
          </div>

          <CheckoutForm stockInsufficient={stockInsufficient} cartItems={cartItems}
            totalAmount={clientCartTotal}
            loyalty={loyalty}
          />
        </aside>
      </div>
    </div>
  );
};

export default Cart;
